import React, { useState } from 'react';
import FileUploader from './FileUploader';

/**
 * Reusable CommentForm component for adding comments with file attachments
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onSubmit - Function to call when the comment is submitted
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {string} props.placeholder - Placeholder text for the textarea
 * @param {string} props.submitLabel - Text for the submit button
 * @returns {JSX.Element} The CommentForm component
 */
const CommentForm = ({ 
  onSubmit, 
  disabled = false, 
  placeholder = "Add your comment...",
  submitLabel = "Comment"
}) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  
  // Initialize file uploader
  const {
    FileInput,
    AttachmentButton,
    FileList,
    dragProps,
    isDragging,
    textareaRef
  } = FileUploader({
    files: filesToUpload,
    setFiles: setFilesToUpload,
    disabled: isSubmitting || disabled
  });
  
  // Handle comment submission
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if ((!comment.trim() && filesToUpload.length === 0) || isSubmitting || disabled) return;
    
    setIsSubmitting(true);
    try {
      // Pass both the comment text and files to the callback
      await onSubmit(comment.trim(), filesToUpload.length > 0 ? filesToUpload : null);
      setComment('');
      setFilesToUpload([]);
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmitComment} className="relative">
      <div 
        className={`border ${isDragging ? 'border-indigo-400 ring-2 ring-indigo-400' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm overflow-hidden`}
        {...dragProps}
      >
        <textarea
          ref={textareaRef}
          rows={3}
          name="comment"
          id="comment"
          className="block w-full py-3 px-3 border-0 resize-none focus:ring-0 focus:outline-none sm:text-sm dark:bg-gray-800 dark:text-white"
          placeholder={placeholder}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={isSubmitting || disabled}
        />

        {/* File attachments area */}
        <FileList />

        {/* Hidden file input */}
        <FileInput />

        {/* Bottom toolbar */}
        <div className="py-2 px-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between dark:bg-gray-800">
          <div>
            <AttachmentButton showText={true} />
          </div>
          <button
            type="submit"
            disabled={(!comment.trim() && filesToUpload.length === 0) || isSubmitting || disabled}
            className={`rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-xs ring-1 ring-inset ${
              (!comment.trim() && filesToUpload.length === 0) || isSubmitting || disabled
                ? "bg-gray-100 text-gray-400 ring-gray-300 cursor-not-allowed"
                : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
            }`}
          >
            {isSubmitting ? "Posting..." : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CommentForm;